import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Briefcase, Globe, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../components/public/Footer';

export default function Sovereignty() {
  const [lang, setLang] = useState('En');

  useEffect(() => {
    const syncLang = () => {
      setLang((localStorage.getItem('januslang') || localStorage.getItem('janus_lang')) === 'Fr' ? 'Fr' : 'En');
    };

    syncLang();
    window.addEventListener('langchange', syncLang);
    window.addEventListener('januslangchanged', syncLang);

    return () => {
      window.removeEventListener('langchange', syncLang);
      window.removeEventListener('januslangchanged', syncLang);
    };
  }, []);

  const translations = {
    Fr: {
      metaTitle: "Sovereignty | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy accompagne la structuration patrimoniale et entrepreneuriale autour de la résidence, de la mobilité du capital, de la détention et de la souveraineté économique.",
      title: "Sovereignty : organiser sa liberté économique avec méthode.",
      sub: "Résidence, structuration entrepreneuriale, mobilité du capital, détention et cohérence patrimoniale : nous aidons à clarifier les bases d'une souveraineté économique concrète.",
      p1: "La souveraineté économique n'est pas un mot d'ordre. C'est une discipline. Elle commence par des questions simples mais décisives : où résider, où détenir, où investir, comment structurer, et comment faire en sorte que les décisions patrimoniales répondent réellement à la trajectoire de vie et d'entreprise. Chez Janus Capital Strategy, cette réflexion s'inscrit dans une approche pratique, internationale et patrimoniale.",
      p2: "À mesure que les réglementations, les fiscalités et les contraintes de circulation du capital se complexifient, la question n'est plus seulement de savoir où investir. La vraie question devient : dans quel cadre organiser durablement son capital, son activité et sa capacité de décision ? Nous abordons la souveraineté comme un sujet de construction : lecture des objectifs, compréhension de la structure actuelle, identification des incohérences, réflexion sur les arbitrages possibles, mise en cohérence avec l'environnement patrimonial global.",
      gridTitle: "Ce que nous entendons par souveraineté",
      cards: [
        {
          title: "Cohérence résidence et structure",
          text: "Nous travaillons la cohérence entre votre résidence et votre structure patrimoniale, ainsi que la lecture entrepreneuriale des juridictions les plus adaptées à vos objectifs de détention.",
          icon: <Briefcase size={24} />
        },
        {
          title: "Mobilité et protection du capital",
          text: "Nous organisons la mobilité et la protection de votre capital à travers une lecture internationale des juridictions, de la détention et des flux, dans une logique de clarté stratégique.",
          icon: <Landmark size={24} />
        },
        {
          title: "Exécution Pas à Pas",
          text: "Création de société (juridiction, statuts, licence), obtention du visa, ouverture de compte bancaire international, et coordination avec notaires et avocats locaux.",
          icon: <Globe size={24} />
        }
      ],
      cta: "Parler de votre structuration",
      ctaSecondary: "Voir le Portefeuille"
    },
    En: {
      metaTitle: "Sovereignty | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy supports wealth and entrepreneurial structuring around residency, capital mobility, ownership, and economic sovereignty.",
      title: "Sovereignty: organizing economic freedom with method.",
      sub: "Residency, entrepreneurial structuring, capital mobility, ownership and wealth coherence: we help clarify the foundations of concrete economic sovereignty.",
      p1: "Economic sovereignty is not a slogan. It is a discipline. It starts with simple but decisive questions: where to reside, where to hold assets, where to invest, how to structure, and how to ensure wealth decisions truly reflect one's life and business trajectory. At Janus Capital Strategy, this reflection takes shape through a practical, international, wealth-oriented approach.",
      p2: "As regulations, taxation and capital-flow constraints grow more complex, the question is no longer simply where to invest. The real question becomes: in what framework should one durably organize their capital, activity and decision-making capacity? We approach sovereignty as a matter of construction: reading objectives, understanding the current structure, identifying inconsistencies, considering possible trade-offs, and aligning with the overall wealth environment.",
      gridTitle: "What we mean by sovereignty",
      cards: [
        {
          title: "Residency and structure coherence",
          text: "We work on the coherence between your residency and your wealth structure, along with an entrepreneurial reading of the jurisdictions best suited to your ownership objectives.",
          icon: <Briefcase size={24} />
        },
        {
          title: "Capital mobility and protection",
          text: "We organize the mobility and protection of your capital through an international reading of jurisdictions, ownership and flows, in a logic of strategic clarity.",
          icon: <Landmark size={24} />
        },
        {
          title: "Step-by-Step Execution",
          text: "Company registration (jurisdiction, structure, license), visa procurement, international bank account opening, and coordination with local notaries and lawyers.",
          icon: <Globe size={24} />
        }
      ],
      cta: "Discuss your structuring",
      ctaSecondary: "View Portfolio"
    },
  };

  const t = translations[lang] || translations.En;
  const isRtl = false;

  return (
    <div className="janus-page font-sans selection:bg-[var(--color-gold)] selection:text-[var(--color-surface)]" dir={isRtl ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta property="og:url" content="https://januscapitalstrategy.com/sovereignty" />
        <meta property="og:image" content="https://januscapitalstrategy.com/uploads/images/SOVEREIGNTY.webp" />
        <meta property="og:site_name" content="Janus Capital" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.metaTitle} />
        <meta name="twitter:description" content={t.metaDesc} />
        <meta name="twitter:image" content="https://januscapitalstrategy.com/uploads/images/SOVEREIGNTY.webp" />
      </Helmet>

      <section className="relative pt-48 pb-32 px-6 lg:px-12 max-w-[1400px] mx-auto text-center border-b border-[var(--color-line)] flex flex-col items-center">
        <h1 className="text-4xl md:text-6xl font-light tracking-wide mb-6 janus-title uppercase">{t.title}</h1>
        <p className="text-[var(--color-gold)] tracking-[0.2em] uppercase text-sm md:text-base font-bold mb-4 text-center max-w-4xl leading-relaxed">{t.sub}</p>
        <img src="https://januscapitalstrategy.com/uploads/images/1784356715968-logo-unifie.webp" alt="Janus Arche" className="h-16 md:h-20 w-auto mt-0 object-contain" />
      </section>

      <section className="py-32 px-6 lg:px-12 max-w-[1200px] mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p1}</p>
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p2}</p>
          </div>
          <div className="w-full aspect-[4/3] sm:aspect-[16/10] relative rounded-sm overflow-hidden shadow-xl border border-[var(--color-line)] janus-surface">
            <img
              src="https://januscapitalstrategy.com/uploads/images/SOVEREIGNTY.webp"
              alt="Sleek Premium Dark Monolithic Towers Emphasizing Corporate Sovereignty"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-32 bg-transparent border-y border-[var(--color-line)] px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-light text-center text-[var(--color-gold)] uppercase tracking-widest mb-16">{t.gridTitle}</h2>
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {t.cards.map((card, idx) => (
              <div
                key={idx}
                className="p-8 border border-[var(--color-line)] janus-surface hover:border-[var(--color-gold)] hover:bg-[var(--color-gold)] transition-all duration-500 group flex flex-col shadow-sm hover:shadow-xl rounded-sm"
              >
                <div className="text-[var(--color-line)] group-hover:text-white/20 transition-colors duration-500 mb-6">
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold tracking-wide uppercase mb-4 janus-title group-hover:text-white transition-colors duration-500">
                  {card.title}
                </h3>
                <p className="janus-text-muted text-sm font-light leading-relaxed group-hover:text-white/90 transition-colors duration-500 flex-grow">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('openContactModal', { detail: { source: 'sovereignty' } }));
                }}
                className="janus-cta-secondary !border-2 !border-[var(--color-black)] hover:!bg-[var(--color-black)] hover:!text-white shadow-xl !px-12 !py-5"
              >
                {t.cta}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
